import {
	CalendarOutlined,
	CheckCircleOutlined,
	DeleteOutlined,
	DollarOutlined,
	EditOutlined,
	FileTextOutlined,
	FilterOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	SearchOutlined,
	UserOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import {
	AutoComplete,
	Button,
	Card,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Statistic,
	Table,
	Typography,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import useBillStore from "../../stores/billStore";
import useEmployeeStore from "../../stores/employeeStore";
import AdminPageLayout from "./layout/AdminPageLayout";

const { Title, Text } = Typography;

const Bill = () => {
	const [form] = Form.useForm();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editingBill, setEditingBill] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const { bills, loading, fetchBills, createBill, updateBill, deleteBill } =
		useBillStore();
	const { employees, fetchEmployees } = useEmployeeStore();

	// Add state for filters - start with current month/year as default
	const [searchText, setSearchText] = useState("");
	const [selectedMonth, setSelectedMonth] = useState(moment().month()); // Default to current month
	const [selectedYear, setSelectedYear] = useState(moment().year()); // Default to current year
	const [selectedBillType, setSelectedBillType] = useState(null);
	const [filteredBills, setFilteredBills] = useState([]);

	useEffect(() => {
		console.log(
			"useEffect triggered - selectedMonth:",
			selectedMonth,
			"selectedYear:",
			selectedYear,
		);

		// Fetch bills with server-side month/year/type/search filtering
		const filters = {};

		// Only add year filter if year is selected
		if (selectedYear !== null) {
			filters.due_date_year = selectedYear;
		}

		// Only add month filter if both year and month are selected
		if (selectedYear !== null && selectedMonth !== null) {
			filters.due_date_month = selectedMonth + 1; // Convert to 1-indexed for backend
		}

		// Add bill type filter
		if (selectedBillType) {
			filters.bill_type = selectedBillType;
		}

		// Add search filter
		if (searchText) {
			filters.search = searchText;
		}

		console.log("Calling fetchBills with filters:", filters);
		fetchBills(filters);
		fetchEmployees();
	}, [selectedMonth, selectedYear, selectedBillType, searchText]); // Re-fetch when any filter changes

	// Filter bills on frontend (minimal filtering if needed)
	useEffect(() => {
		// Since we're doing backend filtering now, just set the bills as filtered
		setFilteredBills(bills || []);
	}, [bills]);

	// Calculate total for displayed bills
	const calculateTotal = () => {
		return filteredBills
			.reduce((total, bill) => {
				return total + parseFloat(bill.amount || 0);
			}, 0)
			.toFixed(2);
	};

	// Generate month options for filter
	const monthOptions = Array.from({ length: 12 }, (_, i) => ({
		value: i,
		label: moment().month(i).format("MMMM"),
	}));

	// Generate year options (current year ± 5 years)
	const currentYear = moment().year();
	const yearOptions = Array.from({ length: 11 }, (_, i) => {
		const year = currentYear - 5 + i;
		return { value: year, label: year.toString() };
	});

	// Handle month change - if year is not selected, month should also be cleared
	const handleMonthChange = (value) => {
		console.log("handleMonthChange called with value:", value);
		// Convert undefined to null for consistency
		const monthValue = value === undefined ? null : value;

		if (monthValue !== null && selectedYear === null) {
			// If trying to select a month without a year, also set current year as default
			setSelectedYear(moment().year());
		}
		setSelectedMonth(monthValue);
	};

	// Handle year change
	const handleYearChange = (value) => {
		console.log("handleYearChange called with value:", value);
		// Convert undefined to null for consistency
		const yearValue = value === undefined ? null : value;

		setSelectedYear(yearValue);
		// If year is cleared, also clear month
		if (yearValue === null) {
			setSelectedMonth(null);
		}
	};
	const getUniqueBillTypes = () => {
		if (!bills) return [];

		const types = [...new Set(bills.map((bill) => bill.bill_type))];
		return types.map((type) => ({
			value: type,
			label: type,
		}));
	};

	const handleOpenModal = (record = null) => {
		setEditingBill(record);
		form.resetFields(); // First reset all fields

		if (record) {
			form.setFieldsValue({
				...record,
				due_date: moment(record.due_date),
			});
		} else {
			// Set status to paid and default due_date to today
			form.setFieldsValue({
				status: "paid",
				due_date: moment(), // Pre-select today's date
			});
		}
		setIsModalVisible(true);
	};

	const handleSubmit = async () => {
		try {
			setSubmitting(true);
			const values = await form.validateFields();
			const formattedValues = {
				...values,
				due_date: values.due_date.format("YYYY-MM-DD"), // Keep the API format unchanged
			};

			if (editingBill) {
				await updateBill(editingBill.id, formattedValues);
			} else {
				await createBill(formattedValues);
			}
			setIsModalVisible(false);
			form.resetFields();

			// Re-fetch bills with current filters
			const filters = {};
			if (selectedYear !== null) {
				filters.due_date_year = selectedYear;
			}
			if (selectedYear !== null && selectedMonth !== null) {
				filters.due_date_month = selectedMonth + 1;
			}
			if (selectedBillType) {
				filters.bill_type = selectedBillType;
			}
			if (searchText) {
				filters.search = searchText;
			}
			fetchBills(filters);
		} catch (error) {
			console.error("Form submission failed:", error);
		} finally {
			setSubmitting(false);
		}
	};

	const billTypeOptions = [
		"Rent",
		"Mortgage",
		"Electricity",
		"Water",
		"Gas",
		"Internet",
		"Furniture",
		"Cleaning Supplies",
		"Cleaning Cost",
		"Check-in Compensation",
		"Maintenance",
		"Repair",
		"Condominium Fees",
		"Insurance",
		"Tax",
		"Amazon Purchases",
		"Other",
	];

	const columns = [
		{
			title: "Bill Type",
			dataIndex: "bill_type",
			key: "bill_type",
		},
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			render: (notes) => (notes ? notes : "N/A"),
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			render: (amount) => `€${parseFloat(amount).toFixed(2)}`,
		},
		{
			title: "Due Date",
			dataIndex: "due_date",
			key: "due_date",
			render: (date) => moment(date).format("DD-MMM"),
		},
		{
			title: "Paid By",
			dataIndex: "paid_by",
			key: "paid_by",
			render: (paid_by) => {
				const employee = employees.find((emp) => emp.id === paid_by);
				if (!employee) return "N/A";

				const nameParts = employee.name.trim().split(" ");
				let initials;
				if (nameParts.length === 1) {
					initials = nameParts[0].charAt(0).toUpperCase();
				} else {
					const firstInitial = nameParts[0].charAt(0).toUpperCase();
					const lastInitial = nameParts[nameParts.length - 1]
						.charAt(0)
						.toUpperCase();
					initials = `${firstInitial}${lastInitial}`;
				}

				return (
					<div
						style={{
							width: "32px",
							height: "32px",
							borderRadius: "50%",
							backgroundColor: "#1890ff",
							color: "white",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "12px",
							fontWeight: "bold",
						}}
					>
						{initials}
					</div>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<Space>
					<Button
						icon={<EditOutlined />}
						onClick={() => handleOpenModal(record)}
					/>
					<Popconfirm
						title="Are you sure you want to delete this bill?"
						onConfirm={() => deleteBill(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<AdminPageLayout
			title="Bills Management"
			icon={<WalletOutlined />}
			subtitle="Manage and track all bills and payments"
			listItems={[
				"View all bills and their status",
				"Add new bills and expenses",
				"Track payment status",
				"Assign payments to employees",
			]}
			actions={
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => handleOpenModal()}
				>
					Add Bill
				</Button>
			}
		>
			{/* Filter and search controls */}
			<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
				<Col xs={24} sm={12} md={5}>
					<Input
						placeholder="Search bills by type or notes"
						prefix={<SearchOutlined />}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						allowClear
					/>
				</Col>
				<Col xs={24} sm={12} md={4}>
					<Select
						placeholder="Month"
						style={{ width: "100%" }}
						options={monthOptions}
						onChange={handleMonthChange}
						value={selectedMonth}
						allowClear={true}
					/>
				</Col>
				<Col xs={24} sm={12} md={4}>
					<Select
						placeholder="Year"
						style={{ width: "100%" }}
						options={yearOptions}
						onChange={handleYearChange}
						value={selectedYear}
						allowClear={true}
					/>
				</Col>
				<Col xs={24} sm={12} md={5}>
					<Select
						placeholder="Filter by bill type"
						style={{ width: "100%" }}
						options={getUniqueBillTypes()}
						onChange={(value) => setSelectedBillType(value)}
						value={selectedBillType}
						allowClear
						onClear={() => setSelectedBillType(null)}
					/>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card>
						<Statistic
							title="Total Amount"
							value={calculateTotal()}
							precision={2}
							prefix="€"
							valueStyle={{ color: "#3f8600" }}
						/>
					</Card>
				</Col>
			</Row>

			<Table
				columns={columns}
				dataSource={filteredBills}
				loading={loading}
				rowKey="id"
				footer={() => {
					let filterText = "all bills";
					if (selectedYear !== null && selectedMonth !== null) {
						filterText = `${moment().month(selectedMonth).format("MMMM")} ${selectedYear}`;
					} else if (selectedYear !== null) {
						filterText = `year ${selectedYear}`;
					}

					return (
						<Row justify="end">
							<Col>
								<Text strong>
									Showing {filteredBills.length} bills for {filterText} | Total:
									€{calculateTotal()}
								</Text>
							</Col>
						</Row>
					);
				}}
			/>

			<Modal
				title={editingBill ? "Edit Bill" : "Add New Bill"}
				open={isModalVisible}
				onOk={handleSubmit}
				onCancel={() => {
					setIsModalVisible(false);
					form.resetFields();
				}}
				confirmLoading={submitting}
				okText={submitting ? "Saving..." : "Save"}
			>
				<Form form={form} layout="vertical" requiredMark="optional">
					<Form.Item
						name="bill_type"
						label={
							<Space>
								<FileTextOutlined />
								Bill Type
							</Space>
						}
						rules={[{ required: true, message: "Please enter the bill type!" }]}
						tooltip="Enter the type of expense or bill to be recorded"
					>
						<AutoComplete
							placeholder="Enter or select bill type"
							options={billTypeOptions.map((option) => ({ value: option }))}
							allowClear
							style={{ width: "100%" }}
							filterOption={(inputValue, option) =>
								option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
								-1
							}
						/>
					</Form.Item>

					<Form.Item
						name="amount"
						label={
							<Space>
								<DollarOutlined />
								Amount
							</Space>
						}
						rules={[{ required: true, message: "Please input the amount!" }]}
						tooltip="The total amount of the bill"
					>
						<InputNumber
							prefix="€"
							style={{ width: "100%" }}
							min={0}
							precision={2}
							placeholder="Enter amount"
						/>
					</Form.Item>

					<Form.Item
						name="due_date"
						label={
							<Space>
								<CalendarOutlined />
								Due Date
							</Space>
						}
						rules={[{ required: true, message: "Please select the due date!" }]}
						tooltip="When the payment is due"
					>
						<DatePicker
							style={{ width: "100%" }}
							placeholder="Select due date"
							format="DD-MMM"
							showToday
							allowClear
							onClick={() => form.setFieldValue("due_date", undefined)}
							onFocus={() => form.setFieldValue("due_date", undefined)}
						/>
					</Form.Item>

					<Form.Item
						name="status"
						label={
							<Space>
								<CheckCircleOutlined />
								Status
							</Space>
						}
						rules={[{ required: true, message: "Please select the status!" }]}
						tooltip="Current payment status of the bill"
						initialValue="paid"
					>
						<Select placeholder="Select status">
							<Select.Option value="unpaid">Unpaid</Select.Option>
							<Select.Option value="paid">Paid</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item
						name="paid_by"
						label={
							<Space>
								<UserOutlined />
								Paid By
							</Space>
						}
						tooltip="Employee responsible for the payment"
					>
						<Select allowClear placeholder="Select employee">
							{employees.map((employee) => (
								<Select.Option key={employee.id} value={employee.id}>
									{employee.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						name="notes"
						label={
							<Space>
								<InfoCircleOutlined />
								Notes
							</Space>
						}
						tooltip="Additional information about the bill"
					>
						<Input.TextArea
							rows={4}
							placeholder="Enter additional notes (optional)"
							showCount
							maxLength={500}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</AdminPageLayout>
	);
};

export default Bill;
